import React from 'react';
import type {
  AssignmentRule,
  BaselineAdmin,
  PermissionsManagementMode,
  PermissionsPolicy,
  PostureInstance
} from './types';
import { KNOWN_GROUP_CATALOG } from './types';
import type { EnterpriseBaselinePolicy } from './types';

export interface PolicyVersionSummary {
  versionId: string;
  versionNumber: number;
  publishedAt: string;
  publishedBy?: string;
  notes?: string;
}

interface PolicyTabProps {
  policy: PermissionsPolicy;
  instances: PostureInstance[];
  saving: boolean;
  onSaveGovernance: (input: { managementMode: PermissionsManagementMode; enforceSystemAdmins: boolean }) => void;
  onAddBaselineAdmin: (admin: BaselineAdmin) => void;
  onRemoveBaselineAdmin: (email: string) => void;
  onAddAssignmentRule: (rule: Omit<AssignmentRule, 'ruleId' | 'isActive'>) => void;
  onRemoveAssignmentRule: (ruleId: string) => void;
  /**
   * When supplied, the tenant Policy editor shows the inherited rows
   * from the Enterprise baseline at the top of the customer system admins
   * and Assignments columns. These rows are read-only at the tenant
   * level — to change them, the operator switches the hero picker
   * to "Enterprise baseline."
   */
  inheritedBaseline?: EnterpriseBaselinePolicy | null;
  /**
   * Hide the Governance column. Used by the Enterprise baseline
   * workspace, where management mode and "enforce baseline" do not
   * apply (the baseline is consumed by tenants; the baseline does
   * not itself enforce anything).
   */
  hideGovernance?: boolean;
  /** Optional override for the top banner. */
  bannerText?: string;
  /**
   * Published versions of this tenant's policy, in DESC order. When
   * empty, the editor presents itself as not-yet-published; when
   * non-empty, it shows the latest published version and the operator
   * can publish a new version.
   */
  versions?: PolicyVersionSummary[];
  /** True while a publish is in flight. */
  publishing?: boolean;
  /** Publishes the current saved policy as a new version. */
  onPublish?: (notes?: string) => void;
}

const MODE_LABEL: Record<PermissionsManagementMode, string> = {
  local: 'Local',
  hybrid: 'Hybrid',
  portal: 'Portal-managed'
};

const MODE_HELP: Record<PermissionsManagementMode, string> = {
  local: 'Each installation manages its own permissions. Portal observes but does not push baseline rules.',
  hybrid: 'Portal-published rules apply alongside any local rules a site has already.',
  portal: 'Every installation uses the Portal-published rules. Local-only rules are not added or kept.'
};

const formatInstance = (instance: PostureInstance): string => {
  try {
    const parsed = new URL(instance.siteUrl);
    return parsed.host + parsed.pathname;
  } catch {
    return instance.siteUrl;
  }
};

/**
 * Tab 2 of /permissions. Three columns: governance + enforce switch,
 * customer system admins (as chips with displayName + email),
 * and assignment rules (as cards with principal / group / target /
 * on-off state). The group reference at the bottom is collapsed by
 * default and read-only — the catalogue is product-managed.
 *
 * When publish/version handlers are supplied, the banner presents the
 * saved policy as a draft that must be published into version history
 * before Apply treats it as the latest policy version.
 */
const formatTimeAgo = (iso?: string): string => {
  if (!iso) return '';
  const parsed = new Date(iso).getTime();
  if (Number.isNaN(parsed)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - parsed) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  return `${Math.floor(seconds / 86400)} days ago`;
};

export const PolicyTab: React.FC<PolicyTabProps> = ({
  policy,
  instances,
  saving,
  onSaveGovernance,
  onAddBaselineAdmin,
  onRemoveBaselineAdmin,
  onAddAssignmentRule,
  onRemoveAssignmentRule,
  inheritedBaseline,
  hideGovernance,
  bannerText,
  versions,
  publishing,
  onPublish
}) => {
  // Governance form is local so the Save button stays useful — we don't
  // want each keystroke firing a network request.
  const [pendingMode, setPendingMode] = React.useState<PermissionsManagementMode>(policy.managementMode);
  const [pendingEnforce, setPendingEnforce] = React.useState<boolean>(policy.enforceSystemAdmins);
  React.useEffect(() => {
    setPendingMode(policy.managementMode);
    setPendingEnforce(policy.enforceSystemAdmins);
  }, [policy.managementMode, policy.enforceSystemAdmins]);

  const governanceDirty =
    pendingMode !== policy.managementMode || pendingEnforce !== policy.enforceSystemAdmins;

  const [newAdminEmail, setNewAdminEmail] = React.useState('');
  const [newAdminName, setNewAdminName] = React.useState('');

  const addBaselineAdmin = (): void => {
    const trimmed = newAdminEmail.trim();
    if (!trimmed) return;
    onAddBaselineAdmin({
      email: trimmed,
      displayName: newAdminName.trim() || undefined
    });
    setNewAdminEmail('');
    setNewAdminName('');
  };

  const [ruleDraftEmail, setRuleDraftEmail] = React.useState('');
  const [ruleDraftKind, setRuleDraftKind] = React.useState<'user' | 'security_group' | 'm365_group'>('user');
  const [ruleDraftIdentifier, setRuleDraftIdentifier] = React.useState('');
  const [ruleDraftName, setRuleDraftName] = React.useState('');
  const [ruleDraftGroupKey, setRuleDraftGroupKey] = React.useState<string>('user');
  const [ruleDraftTargetMode, setRuleDraftTargetMode] = React.useState<'all_instances' | 'instances'>('instances');
  const [ruleDraftInstanceIds, setRuleDraftInstanceIds] = React.useState<string[]>([]);

  const addRule = (): void => {
    const principalEmail = ruleDraftEmail.trim();
    const principalIdentifier = (ruleDraftKind === 'user' ? principalEmail : ruleDraftIdentifier).trim();
    if (!principalIdentifier) return;
    const targetInstanceIds = ruleDraftTargetMode === 'all_instances'
      ? []
      : ruleDraftInstanceIds.filter((value) => value);
    if (ruleDraftTargetMode === 'instances' && targetInstanceIds.length === 0) {
      return;
    }
    onAddAssignmentRule({
      groupKey: ruleDraftGroupKey,
      principalKind: ruleDraftKind,
      principalIdentifier,
      principalEmail: principalEmail || undefined,
      principalDisplayName: ruleDraftName.trim() || undefined,
      targetMode: ruleDraftTargetMode,
      targetInstanceIds
    });
    setRuleDraftEmail('');
    setRuleDraftKind('user');
    setRuleDraftIdentifier('');
    setRuleDraftName('');
    setRuleDraftGroupKey('user');
    setRuleDraftTargetMode('instances');
    setRuleDraftInstanceIds([]);
  };

  const [referenceOpen, setReferenceOpen] = React.useState(false);
  const [versionsOpen, setVersionsOpen] = React.useState(false);
  const [publishNotes, setPublishNotes] = React.useState('');

  const publishEnabled = typeof onPublish === 'function';
  const latestVersion = versions && versions.length > 0 ? versions[0] : undefined;
  const draftAheadOfPublished = Boolean(
    publishEnabled &&
    policy.updatedAt &&
    latestVersion &&
    new Date(policy.updatedAt).getTime() > new Date(latestVersion.publishedAt).getTime()
  );
  const noVersionsYet = publishEnabled && (!versions || versions.length === 0);

  const computedBanner = (() => {
    if (bannerText) return bannerText;
    if (!publishEnabled) {
      return 'Editing the live policy. Saves change the active policy immediately.';
    }
    if (noVersionsYet) {
      return 'Edits save to draft. This tenant has no published policy yet — click Publish to make the current draft live.';
    }
    if (draftAheadOfPublished) {
      return `Edits save to draft. Latest published v${latestVersion?.versionNumber} ${formatTimeAgo(latestVersion?.publishedAt)}${latestVersion?.publishedBy ? ` by ${latestVersion.publishedBy}` : ''}. Click Publish to make the current draft live.`;
    }
    return `Live policy is v${latestVersion?.versionNumber}, published ${formatTimeAgo(latestVersion?.publishedAt)}${latestVersion?.publishedBy ? ` by ${latestVersion.publishedBy}` : ''}. New edits save to draft until Published.`;
  })();

  const handlePublishClick = (): void => {
    if (!onPublish) return;
    const notes = publishNotes.trim();
    onPublish(notes.length > 0 ? notes : undefined);
    setPublishNotes('');
  };

  return (
    <div className="permissions-policy-tab">
      {/* Banner — describes draft / publish flow now that version endpoints exist. */}
      <div className="policy-banner">
        <span className="band-icon" aria-hidden="true">ℹ</span>
        <span className="band-text">{computedBanner}</span>
        {publishEnabled && (
          <div className="policy-banner-actions">
            <input
              type="text"
              className="input"
              placeholder="Publish note (optional)"
              value={publishNotes}
              maxLength={1000}
              disabled={!!publishing}
              onChange={(event) => setPublishNotes(event.target.value)}
              aria-label="Publish note"
            />
            <button
              type="button"
              className="btn primary"
              onClick={handlePublishClick}
              disabled={!!publishing}
            >
              {publishing ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        )}
      </div>

      {publishEnabled && versions && versions.length > 0 && (
        <details
          className="policy-versions"
          open={versionsOpen}
          onToggle={(event) => setVersionsOpen((event.target as HTMLDetailsElement).open)}
        >
          <summary>Version history ({versions.length})</summary>
          <ol className="policy-versions-list">
            {versions.map((version) => (
              <li key={version.versionId} className="policy-version-row">
                <span className="policy-version-tag">v{version.versionNumber}</span>
                <span className="policy-version-meta">
                  {formatTimeAgo(version.publishedAt)}
                  {version.publishedBy ? ` · ${version.publishedBy}` : ''}
                </span>
                {version.notes && <span className="policy-version-notes">{version.notes}</span>}
              </li>
            ))}
          </ol>
        </details>
      )}

      <div className="permissions-policy-grid">
        {/* Governance column — hidden in baseline-layer mode */}
        {!hideGovernance && <section className="policy-section">
          <h3>Governance</h3>
          <p className="card-intro">How permissions move between Portal and installations.</p>

          <label className="policy-field">
            <span className="policy-field-label">Mode</span>
            <select
              className="input"
              value={pendingMode}
              disabled={saving}
              onChange={(event) => setPendingMode(event.target.value as PermissionsManagementMode)}
            >
              {(['local', 'hybrid', 'portal'] as PermissionsManagementMode[]).map((mode) => (
                <option key={mode} value={mode}>{MODE_LABEL[mode]}</option>
              ))}
            </select>
            <span className="policy-field-help">{MODE_HELP[pendingMode]}</span>
          </label>

          <label className="policy-field policy-field-inline">
            <input
              type="checkbox"
              checked={pendingEnforce}
              disabled={saving}
              onChange={(event) => setPendingEnforce(event.target.checked)}
            />
            <span>Enforce baseline System Administrators on every site</span>
          </label>

          <div className="policy-actions">
            <button
              type="button"
              className="btn"
              disabled={!governanceDirty || saving}
              onClick={() => onSaveGovernance({ managementMode: pendingMode, enforceSystemAdmins: pendingEnforce })}
            >
              {saving ? 'Saving…' : 'Save governance'}
            </button>
            {governanceDirty && !saving && (
              <span className="policy-actions-hint">Unsaved changes</span>
            )}
          </div>
        </section>}

        {/* Customer system admins column */}
        <section className="policy-section">
          <h3>Customer system admins</h3>
          <p className="card-intro">
            People trusted by the customer to administer Edinshall Audit. They are added to
            <em> Edinshall System Administrators</em> on covered sites and can use the portal's
            customer-admin controls for this licensed tenant scope.
          </p>

          {inheritedBaseline && inheritedBaseline.baselineAdmins.length > 0 && (
            <div className="policy-inherited-section">
              <div className="policy-inherited-label">From the Enterprise baseline · read-only here</div>
              <div className="policy-chip-list">
                {inheritedBaseline.baselineAdmins.map((admin) => (
                  <span key={admin.email} className="policy-chip is-inherited" title="Edit on the Enterprise baseline">
                    <span className="policy-chip-text">
                      {admin.displayName ? <strong>{admin.displayName}</strong> : null}
                      {admin.displayName ? ' ' : ''}
                      <span className="muted">{admin.email}</span>
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="policy-chip-list">
            {policy.baselineSystemAdmins.length === 0 && (
              <span className="policy-empty">No customer system admins configured.</span>
            )}
            {policy.baselineSystemAdmins.map((admin) => (
              <span key={admin.email} className="policy-chip">
                <span className="policy-chip-text">
                  {admin.displayName ? <strong>{admin.displayName}</strong> : null}
                  {admin.displayName ? ' ' : ''}
                  <span className="muted">{admin.email}</span>
                </span>
                <button
                  type="button"
                  className="policy-chip-remove"
                  aria-label={`Remove ${admin.email}`}
                  disabled={saving}
                  onClick={() => onRemoveBaselineAdmin(admin.email)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <div className="policy-inline-form">
            <input
              className="input"
              type="email"
              placeholder="email@example.com"
              value={newAdminEmail}
              disabled={saving}
              onChange={(event) => setNewAdminEmail(event.target.value)}
            />
            <input
              className="input"
              type="text"
              placeholder="Display name (optional)"
              value={newAdminName}
              disabled={saving}
              onChange={(event) => setNewAdminName(event.target.value)}
            />
            <button
              type="button"
              className="btn secondary"
              disabled={saving || !newAdminEmail.trim()}
              onClick={addBaselineAdmin}
            >
              Add
            </button>
          </div>
        </section>

        {/* Assignment rules column */}
        <section className="policy-section">
          <h3>Assignments</h3>
          <p className="card-intro">
            People or directory groups to add on all or some installations. Use one directory group per role on each installation. Membership is managed in your directory; nested groups and distribution lists are not supported.
          </p>

          {inheritedBaseline && inheritedBaseline.assignmentRules.length > 0 && (
            <div className="policy-inherited-section">
              <div className="policy-inherited-label">From the Enterprise baseline · read-only here</div>
              <div className="policy-rule-list">
                {inheritedBaseline.assignmentRules.map((rule) => {
                  const groupLabel = KNOWN_GROUP_CATALOG.find((entry) => entry.key === rule.groupKey)?.name ?? rule.groupKey;
                  const targetText = rule.targetMode === 'all_instances'
                    ? 'All installations'
                    : `${rule.targetInstanceIds.length} installation${rule.targetInstanceIds.length === 1 ? '' : 's'}`;
                  return (
                    <article key={rule.ruleId} className="policy-rule-card is-inherited">
                      <div className="policy-rule-row">
                        <span className="policy-rule-chip">
                          {rule.principalDisplayName ? <strong>{rule.principalDisplayName}</strong> : null}
                          {rule.principalDisplayName ? ' ' : ''}
                          <span className="muted">{rule.principalKind === 'm365_group' ? 'Microsoft 365 group: ' : rule.principalKind === 'security_group' ? 'Entra security group: ' : ''}{rule.principalIdentifier ?? rule.principalEmail}</span>
                        </span>
                        <span className="policy-rule-chip">{groupLabel}</span>
                        <span className="policy-rule-chip">{targetText}</span>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}

          <div className="policy-rule-list">
            {policy.assignmentRules.length === 0 && (
              <span className="policy-empty">No assignment rules configured.</span>
            )}
            {policy.assignmentRules.map((rule) => {
              const targetText = rule.targetMode === 'all_instances'
                ? 'All installations'
                : `${rule.targetInstanceIds.length} installation${rule.targetInstanceIds.length === 1 ? '' : 's'}`;
              const groupLabel = KNOWN_GROUP_CATALOG.find((entry) => entry.key === rule.groupKey)?.name ?? rule.groupKey;
              return (
                <article key={rule.ruleId} className="policy-rule-card">
                  <div className="policy-rule-row">
                    <span className="policy-rule-chip">
                      {rule.principalDisplayName ? <strong>{rule.principalDisplayName}</strong> : null}
                      {rule.principalDisplayName ? ' ' : ''}
                      <span className="muted">{rule.principalKind === 'm365_group' ? 'Microsoft 365 group: ' : rule.principalKind === 'security_group' ? 'Entra security group: ' : ''}{rule.principalIdentifier ?? rule.principalEmail}</span>
                    </span>
                    <span className="policy-rule-chip">{groupLabel}</span>
                    <span className="policy-rule-chip">{targetText}</span>
                    {!rule.isActive && <span className="policy-rule-chip warning">inactive</span>}
                  </div>
                  <div className="policy-rule-actions">
                    <button
                      type="button"
                      className="btn secondary btn-small"
                      disabled={saving}
                      onClick={() => onRemoveAssignmentRule(rule.ruleId)}
                    >
                      Remove
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="policy-rule-add">
            <div className="policy-rule-add-row">
              <label className="policy-rule-add-label">
                <span>Principal type</span>
                <select className="input" value={ruleDraftKind} disabled={saving} onChange={(event) => {
                  setRuleDraftKind(event.target.value as 'user' | 'security_group' | 'm365_group');
                  setRuleDraftEmail('');
                  setRuleDraftIdentifier('');
                }}>
                  <option value="user">Person</option>
                  <option value="security_group">Entra security group</option>
                  <option value="m365_group">Microsoft 365 group (members)</option>
                </select>
              </label>
              <input
                className="input"
                type={ruleDraftKind === 'user' ? 'email' : 'text'}
                aria-label={ruleDraftKind === 'user' ? 'Person email' : 'Directory group object ID'}
                placeholder={ruleDraftKind === 'user' ? 'email@example.com' : 'Directory group object ID'}
                value={ruleDraftKind === 'user' ? ruleDraftEmail : ruleDraftIdentifier}
                disabled={saving}
                onChange={(event) => ruleDraftKind === 'user' ? setRuleDraftEmail(event.target.value) : setRuleDraftIdentifier(event.target.value)}
              />
              <input
                className="input"
                type="text"
                placeholder="Display name (optional)"
                value={ruleDraftName}
                disabled={saving}
                onChange={(event) => setRuleDraftName(event.target.value)}
              />
            </div>
            <div className="policy-rule-add-row">
              <label className="policy-rule-add-label">
                <span>Group</span>
                <select
                  className="input"
                  value={ruleDraftGroupKey}
                  disabled={saving}
                  onChange={(event) => setRuleDraftGroupKey(event.target.value)}
                >
                  {KNOWN_GROUP_CATALOG.map((group) => (
                    <option key={group.key} value={group.key}>{group.name}</option>
                  ))}
                </select>
              </label>
              <label className="policy-rule-add-label">
                <span>Target</span>
                <select
                  className="input"
                  value={ruleDraftTargetMode}
                  disabled={saving}
                  onChange={(event) => {
                    const next = event.target.value as 'all_instances' | 'instances';
                    setRuleDraftTargetMode(next);
                    if (next === 'all_instances') setRuleDraftInstanceIds([]);
                  }}
                >
                  <option value="instances">Specific installations</option>
                  <option value="all_instances">All installations</option>
                </select>
              </label>
            </div>
            {ruleDraftTargetMode === 'instances' && instances.length > 0 && (
              <label className="policy-rule-add-label">
                <span>Installations</span>
                <select
                  className="input"
                  multiple
                  size={Math.min(5, instances.length)}
                  value={ruleDraftInstanceIds}
                  disabled={saving}
                  onChange={(event) => {
                    const next = Array.from(event.target.selectedOptions).map((option) => option.value);
                    setRuleDraftInstanceIds(next);
                  }}
                >
                  {instances
                    .filter((entry) => entry.status !== 'deregistered')
                    .map((instance) => (
                      <option key={instance.instanceId} value={instance.instanceId}>
                        {formatInstance(instance)}
                      </option>
                    ))}
                </select>
              </label>
            )}
            <div className="policy-actions">
              <button
                type="button"
                className="btn secondary"
                disabled={
                  saving
                  || !(ruleDraftKind === 'user' ? ruleDraftEmail : ruleDraftIdentifier).trim()
                  || (ruleDraftTargetMode === 'instances' && ruleDraftInstanceIds.length === 0)
                }
                onClick={addRule}
              >
                Add rule
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Group reference — collapsed by default, read-only. */}
      <section className="policy-section policy-reference">
        <button
          type="button"
          className="policy-reference-toggle"
          aria-expanded={referenceOpen}
          onClick={() => setReferenceOpen((value) => !value)}
        >
          <span aria-hidden="true">{referenceOpen ? '▾' : '▸'}</span>
          <span>Group reference</span>
          <span className="muted">{KNOWN_GROUP_CATALOG.length} groups in the product catalogue</span>
        </button>
        {referenceOpen && (
          <dl className="policy-reference-list">
            {KNOWN_GROUP_CATALOG.map((group) => (
              <React.Fragment key={group.key}>
                <dt>{group.name}</dt>
                <dd>{group.description}</dd>
              </React.Fragment>
            ))}
          </dl>
        )}
      </section>
    </div>
  );
};

export default PolicyTab;
